// src/auth/session.ts — SCE Issues
// Session gardée (Lot 10) : ouverture silencieuse au démarrage, jetons,
// connexion et déconnexion.
//
// Pourquoi : la session MSAL était rangée dans le sessionStorage, propre à
// chaque onglet. Un lien cliqué depuis un mail Power Automate ou Teams ouvre
// un onglet neuf, donc sans session → page de connexion à chaque fois.
// Désormais :
//
//  1. le cache MSAL est dans le localStorage (msalConfig) : partagé par tous
//     les onglets, il survit à la fermeture du navigateur ;
//  2. au démarrage, on ouvre la session SANS écran si c'est possible : jeton
//     en cache, puis session Microsoft du navigateur (ssoSilent, iframe
//     cachée — marche aussi avec la session Windows du poste) ;
//  3. toute connexion interactive passe par REDIRECTION : les popups sont
//     bloquées dans le navigateur intégré d'Outlook et de Teams, et souvent
//     sur mobile. Au retour, MSAL rouvre la page de départ
//     (navigateToLoginRequestUrl) : un lien de mail mène bien à sa fiche.
//
// Une déconnexion VOLONTAIRE ne quitte pas la page (seule la session de
// l'application est vidée) et elle est retenue dans le navigateur : sans ça,
// la reconnexion silencieuse (session Windows) rouvrirait la session aussitôt.

import {
  BrowserAuthError,
  BrowserAuthErrorCodes,
  InteractionRequiredAuthError,
  type AccountInfo,
  type SilentRequest,
} from '@azure/msal-browser'
import { loginRequest, msalInstance, tokenRequest } from './msalConfig'
import { oublierPersonnes } from '@/auth/comptePartage'

const CLE_DECONNEXION = 'sce-issues:deconnexion-volontaire'

/** Pourquoi l'écran de connexion s'affiche, quand il y a une raison à dire. */
export type MotifConnexion = 'deconnexion' | 'echec'

export interface EtatSession {
  connecte: boolean
  /** Déconnexion volontaire, ou retour de connexion raté (cf. EcranConnexion). */
  motif: MotifConnexion | null
}

/** La page part vers Microsoft : on attend la navigation, sans jamais conclure. */
function attendreNavigation<T>(): Promise<T> {
  return new Promise<T>(() => {})
}

export function compteConnu(): AccountInfo | null {
  return msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0] ?? null
}

/** L'utilisateur s'est déconnecté lui-même et n'a pas encore recliqué « Se connecter ». */
export function deconnexionVolontaire(): boolean {
  try {
    return localStorage.getItem(CLE_DECONNEXION) === '1'
  } catch {
    return false
  }
}

function retenirDeconnexion(volontaire: boolean): void {
  try {
    if (volontaire) localStorage.setItem(CLE_DECONNEXION, '1')
    else localStorage.removeItem(CLE_DECONNEXION)
  } catch {
    // Stockage indisponible : au pire, la reconnexion silencieuse rouvre la
    // session après une déconnexion. Rien de bloquant.
  }
}

/**
 * Démarre MSAL et ouvre la session si possible sans rien demander.
 *
 * Ordre :
 *  1. retour d'une redirection de connexion → connecté ;
 *  2. compte en cache et jeton obtenu en silence → connecté ;
 *  3. compte en cache mais Microsoft exige une interaction (session expirée)
 *     → redirection directe, avec le compte pré-rempli : c'est ce que ferait
 *     le clic sur « Se connecter », et c'est souvent transparent (session
 *     Windows, cookie Microsoft) ;
 *  4. aucun compte → ssoSilent, sauf après une déconnexion volontaire ;
 *  5. sinon → écran de connexion.
 */
export async function demarrerSession(): Promise<EtatSession> {
  await msalInstance.initialize()

  // (MsalProvider rappelle handleRedirectPromise à son montage : MSAL rend
  // alors la même promesse, la réponse n'est pas traitée deux fois.)
  let retourEchoue = false
  try {
    const retour = await msalInstance.handleRedirectPromise()
    if (retour?.account) {
      msalInstance.setActiveAccount(retour.account)
      retenirDeconnexion(false)
      return { connecte: true, motif: null }
    }
  } catch (err) {
    // Connexion annulée ou refusée chez Microsoft. Surtout ne pas relancer
    // de redirection automatique derrière : ce serait une boucle.
    retourEchoue = true
    // eslint-disable-next-line no-console
    console.error('[session] retour de connexion en échec:', err)
  }

  const compte = compteConnu()
  if (compte) {
    msalInstance.setActiveAccount(compte)
    try {
      await msalInstance.acquireTokenSilent({ ...tokenRequest, account: compte })
      return { connecte: true, motif: null }
    } catch (err) {
      if (!(err instanceof InteractionRequiredAuthError)) {
        // Réseau, Microsoft indisponible… : ce n'est pas une question de
        // session. On entre ; les pages afficheront leurs propres erreurs.
        // eslint-disable-next-line no-console
        console.warn('[session] jeton silencieux en échec:', err)
        return { connecte: true, motif: null }
      }
      if (retourEchoue) return { connecte: false, motif: 'echec' }
      await msalInstance.acquireTokenRedirect({
        ...tokenRequest,
        account: compte,
        loginHint: compte.username,
      })
      return attendreNavigation()
    }
  }

  if (!deconnexionVolontaire() && !retourEchoue) {
    try {
      const reponse = await msalInstance.ssoSilent({ scopes: [...loginRequest.scopes] })
      if (reponse.account) {
        msalInstance.setActiveAccount(reponse.account)
        return { connecte: true, motif: null }
      }
    } catch {
      // Pas de session Microsoft exploitable dans ce navigateur (ou cookies
      // tiers bloqués) : cas normal d'une première visite.
    }
  }

  if (retourEchoue) return { connecte: false, motif: 'echec' }
  return { connecte: false, motif: deconnexionVolontaire() ? 'deconnexion' : null }
}

/** Connexion par redirection (bouton « Se connecter »). */
export async function connecter(): Promise<void> {
  retenirDeconnexion(false)
  await msalInstance.loginRedirect(loginRequest)
}

/**
 * Déconnexion de SCE Issues SEULEMENT, sans quitter la page (retour de
 * recette du 25/09) : le cache MSAL de l'application est vidé, mais on ne
 * part pas chez Microsoft — sa page de déconnexion demandait de choisir un
 * compte, et fermait au passage Outlook et Teams dans ce navigateur.
 * msal-react voit le compte disparaître et affiche l'écran de connexion, avec
 * « Vous êtes déconnecté » (déconnexion retenue, cf. deconnexionVolontaire).
 */
export async function deconnecter(): Promise<void> {
  retenirDeconnexion(true)
  // Appareil partagé : le prochain qui se connecte dira qui il est.
  oublierPersonnes()
  await msalInstance.logoutRedirect({
    account: compteConnu(),
    onRedirectNavigate: () => false,
  })
}

/** Popup refusée par le navigateur (Outlook, Teams, mobile) — pas annulée par l'utilisateur. */
function popupBloquee(err: unknown): boolean {
  if (!(err instanceof BrowserAuthError)) return false
  const codes: string[] = [
    BrowserAuthErrorCodes.popupWindowError,
    BrowserAuthErrorCodes.emptyWindowError,
    BrowserAuthErrorCodes.blockNestedPopups,
    BrowserAuthErrorCodes.monitorWindowTimeout,
  ]
  return codes.includes(err.errorCode)
}

/**
 * Jeton d'accès pour une audience donnée (Graph ou SharePoint REST).
 *
 * Silencieux d'abord. Si Microsoft exige une interaction EN COURS
 * D'UTILISATION (page ouverte depuis plus d'un jour, consentement…) : popup,
 * qui garde la page et une saisie en cours ; si le navigateur la bloque,
 * redirection — la page est rouverte au même endroit après connexion.
 */
export async function obtenirJeton(requete: SilentRequest, account: AccountInfo): Promise<string> {
  try {
    return (await msalInstance.acquireTokenSilent({ ...requete, account })).accessToken
  } catch (err) {
    if (!(err instanceof InteractionRequiredAuthError)) throw err
  }
  try {
    return (await msalInstance.acquireTokenPopup({ ...requete, account })).accessToken
  } catch (err) {
    if (!popupBloquee(err)) throw err
  }
  await msalInstance.acquireTokenRedirect({ ...requete, account })
  return attendreNavigation()
}
